import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import ShareBar from '../components/ShareBar';

const SMART_MONEY_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Smart Money Tracker — Hedge Fund & Insider Trade Data',
  description: 'Track real SEC insider trades and hedge fund portfolios from Berkshire Hathaway, Citadel, Renaissance Technologies, Pershing Square, Point72 and more. Updated daily from official EDGAR filings.',
  url: 'https://knowfi.io/smart-money',
  publisher: { '@type': 'Organization', name: 'KnowFi', url: 'https://knowfi.io' },
  mainEntity: {
    '@type': 'Dataset',
    name: 'Hedge Fund 13F Holdings & Insider Trades',
    description: 'Quarterly 13F SEC filings and Form 4 insider transactions for top hedge funds including Berkshire Hathaway, Citadel Advisors, Renaissance Technologies, Pershing Square, Point72, Tiger Global, Appaloosa, Baupost Group, and Soros Fund Management.',
    url: 'https://knowfi.io/smart-money',
    creator: { '@type': 'Organization', name: 'KnowFi' },
    keywords: 'hedge fund portfolio, insider trading, 13F filings, Warren Buffett portfolio, Citadel holdings, Berkshire Hathaway stocks, SEC filings, smart money tracker',
  },
};

const SMART_MONEY_FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'What is the Smart Money Tracker?', acceptedAnswer: { '@type': 'Answer', text: 'The Smart Money Tracker shows real SEC EDGAR filings — insider trades (Form 4) and quarterly hedge fund holdings (13F) — from the world\'s top investors including Warren Buffett, Ken Griffin, Bill Ackman, and Steve Cohen.' } },
    { '@type': 'Question', name: 'How often is the data updated?', acceptedAnswer: { '@type': 'Answer', text: 'Insider trades are updated daily. Hedge fund 13F portfolio holdings update when new SEC filings are submitted, typically quarterly.' } },
    { '@type': 'Question', name: 'What hedge funds are tracked?', acceptedAnswer: { '@type': 'Answer', text: 'We track Berkshire Hathaway (Warren Buffett), Citadel Advisors (Ken Griffin), Renaissance Technologies (Jim Simons), Pershing Square (Bill Ackman), Point72 (Steve Cohen), Tiger Global (Chase Coleman), Appaloosa Management (David Tepper), Baupost Group (Seth Klarman), and Soros Fund Management (George Soros).' } },
    { '@type': 'Question', name: 'What is a 13F filing?', acceptedAnswer: { '@type': 'Answer', text: 'A 13F is a quarterly SEC filing required of institutional investment managers with over $100 million in assets. It discloses all equity holdings, letting the public see exactly what top hedge funds own.' } },
  ],
};

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function getIsPro() {
  try {
    const p = JSON.parse(localStorage.getItem('knowfi_user') || '{}').plan || '';
    return p.toLowerCase() === 'pro';
  } catch { return false; }
}



// ─── Colors ──────────────────────────────────────────────────────────────────
const AVATAR_PALETTES = [
  ['#1e3a5f','#38bdf8'],['#1f1a45','#a78bfa'],['#1a3520','#22c55e'],
  ['#3d1a1a','#f87171'],['#2d1f00','#f59e0b'],
];
function avatarStyle(name) {
  const i = (name?.charCodeAt(0) || 0) % AVATAR_PALETTES.length;
  return {
    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, fontWeight: 800, color: AVATAR_PALETTES[i][1],
    background: AVATAR_PALETTES[i][0], letterSpacing: 0.5,
  };
}
function initials(name) {
  if (!name) return '?';
  const parts = name.replace(/\bInc\b|\bCorp\b|\bLtd\b/gi, '').trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.slice(0,2).toUpperCase();
}

function tradeLabel(trade) {
  // Prefer ticker; fall back to initials from company name
  if (trade.ticker) return trade.ticker.slice(0, 4);
  return initials(trade.company);
}

function tradeAvatarStyle(trade) {
  // Use ticker-based color if available (consistent per ticker), else name-based
  return avatarStyle(trade.ticker || trade.company);
}

// ─── Ghost data for blur preview ─────────────────────────────────────────────
const GHOST_TRADES = [
  { company: 'Tech Giant Inc', insider: 'CEO', date: 'Mar 29' },
  { company: 'Energy Corp', insider: 'CFO', date: 'Mar 28' },
];
const GHOST_HOLDINGS = [
  { name: 'Major Corp A', pct: '8.4%', valueFormatted: '$5.2B' },
  { name: 'Major Corp B', pct: '6.1%', valueFormatted: '$3.8B' },
];

// ─── Component ───────────────────────────────────────────────────────────────
export default function SmartMoney() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isPro = getIsPro();
  // All cards start collapsed; user expands what they want to read
  const [collapsed, setCollapsed] = useState(() => new Set(['insider-trades', 'berkshire-hathaway']));
  const toggleCollapsed = id => setCollapsed(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // Collapse all hedge fund cards by default once data arrives
  useEffect(() => {
    if (!data?.hedgeFunds) return;
    setCollapsed(prev => {
      const n = new Set(prev);
      data.hedgeFunds.filter(f => !f.fund?.toLowerCase().includes('berkshire')).forEach(f => {
        const id = f.fund?.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');
        n.add(id);
      });
      return n;
    });
  }, [data]);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetch(`${API}/api/smart-money${isPro ? '?tier=pro' : ''}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [isPro]);

  // Scroll to fund when ?fund= param is present
  useEffect(() => {
    const fundParam = searchParams.get('fund');
    if (!fundParam || !data) return;
    const el = document.getElementById(`fund-${fundParam}`);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [searchParams, data]);

  const trades = data?.insiderTrades || [];
  const hedgeFunds = data?.hedgeFunds || [];
  const freeTrades = trades.slice(0, 3);
  const proTrades = trades.slice(3);
  const lastUpdated = data?.lastUpdatedFormatted || (data?.lastUpdated
    ? new Date(data.lastUpdated).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago', timeZoneName: 'short' })
    : null);

  return (
    <div style={{ minHeight: '100vh', background: '#080f1e', paddingBottom: 80 }}>
      <SEO
        title="Smart Money Tracker — Hedge Fund Holdings & Insider Trades"
        description="See exactly what Warren Buffett, Ken Griffin, Bill Ackman, Steve Cohen and 5 other elite investors own right now. Real SEC 13F filings and insider trades — updated daily."
        path="/smart-money"
        keywords="Warren Buffett portfolio 2025, hedge fund holdings, 13F filings, Ken Griffin portfolio, insider trades, Berkshire Hathaway stocks"
      />
      <Helmet>
        <meta name="keywords" content="hedge fund tracker, insider trading SEC, 13F filings, Warren Buffett portfolio 2025, Berkshire Hathaway holdings, Citadel Advisors portfolio, smart money tracker, what hedge funds are buying, institutional investor holdings" />
        <script type="application/ld+json">{JSON.stringify(SMART_MONEY_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(SMART_MONEY_FAQ_JSONLD)}</script>
      </Helmet>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .sm-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 28px; }
        .sm-fund-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }
        .sm-holding-row { display: flex; align-items: center; gap: 10px; padding: 9px 0; border-bottom: 1px solid rgba(255,255,255,0.04); }
        .sm-holding-meta { display: flex; gap: 10px; align-items: center; flex-shrink: 0; }
        .sm-trade-row { display: flex; align-items: center; gap: 10px; padding: 12px 0; border-bottom: 1px solid rgba(255,255,255,0.04); }
        .sm-trade-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }

        @media (max-width: 700px) {
          .sm-grid { grid-template-columns: 1fr !important; }
          .sm-fund-grid { grid-template-columns: 1fr !important; }
          .sm-holding-meta { flex-direction: column; align-items: flex-end; gap: 3px; }
          .sm-trade-right { flex-direction: column; align-items: flex-end; gap: 4px; }
          .sm-trade-row { flex-wrap: nowrap; align-items: flex-start; }
        }
      `}</style>

      {/* ── Hero ── */}
      <div style={{
        background: 'linear-gradient(160deg, #0d1b35 0%, #13103a 50%, #0d1b35 100%)',
        borderBottom: '1px solid rgba(56,189,248,0.12)',
        padding: '52px 20px 44px', textAlign: 'center',
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)',
          borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 700,
          color: '#38bdf8', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 18,
        }}>
          🧠 Smart Money Tracker
        </div>
        <h1 style={{ fontSize: 'clamp(26px,5vw,44px)', fontWeight: 900, margin: '0 0 14px', lineHeight: 1.1 }}>
          <span style={{ color: '#f1f5f9' }}>Follow the </span>
          <span style={{
            background: 'linear-gradient(135deg, #38bdf8 0%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>Smart Money</span>
        </h1>
        <p style={{ fontSize: 'clamp(14px,2vw,17px)', color: '#94a3b8', maxWidth: 520, margin: '0 auto 24px', lineHeight: 1.6 }}>
          Real SEC filings. See what corporate insiders and elite hedge funds are buying — before it's mainstream news.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, flexWrap: 'wrap' }}>
          {[
            ['🟢', 'Live SEC data'],
            ['📅', 'Updated daily'],
            ['🏛️', '5 top hedge funds'],
            lastUpdated ? ['🕐', `Last refresh: ${lastUpdated}`] : null,
          ].filter(Boolean).map(([icon, label], i) => (
            <span key={i} style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>{icon} {label}</span>
          ))}
        </div>
      </div>

      {/* ── Loading / Error ── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 80, color: '#64748b', fontSize: 15 }}>
          <div style={{ width: 36, height: 36, border: '3px solid rgba(56,189,248,0.2)', borderTop: '3px solid #38bdf8', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 14px' }} />
          Loading market intelligence…
        </div>
      )}
      {error && (
        <div style={{ textAlign: 'center', padding: 80, color: '#f87171', fontSize: 15 }}>
          Could not load data. Check back in a moment.
        </div>
      )}

      {!loading && !error && (
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '36px 16px 0' }}>

          {/* ── Why This Page Exists ── */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(56,189,248,0.06), rgba(167,139,250,0.06))',
            border: '1px solid rgba(56,189,248,0.15)', borderRadius: 16,
            padding: '24px 28px', marginBottom: 32,
          }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
              📖 Why this page exists
            </div>
            <p style={{ fontSize: 15, color: '#cbd5e1', lineHeight: 1.75, margin: '0 0 14px' }}>
              The biggest investors in the world — hedge fund managers and corporate executives — are legally required to report their trades to the SEC.
              That means you can see exactly what stocks they're buying and selling.
            </p>
            <p style={{ fontSize: 15, color: '#cbd5e1', lineHeight: 1.75, margin: '0 0 20px' }}>
              This page pulls that data for you automatically. <strong style={{ color: '#f1f5f9' }}>You don't need to copy them</strong> — but knowing where smart money is flowing can help you spot trends, avoid sectors being quietly dumped, and feel more confident in your own decisions.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {[
                { icon: '🕵️', title: 'Insider Trades', body: 'When a CEO buys their own stock, it usually means they think the price is going up. They know the company better than anyone.' },
                { icon: '🏦', title: 'Hedge Fund Holdings', body: 'These funds manage billions and have entire research teams. What they own gives you a window into where institutional money is betting long-term.' },
                { icon: '🍎', title: 'Berkshire Hathaway', body: 'Warren Buffett\'s portfolio is the most-watched in the world. His buys and sells signal conviction on specific businesses and entire sectors.' },
              ].map((c, i) => (
                <div key={i} style={{ background: 'rgba(15,28,48,0.6)', borderRadius: 12, padding: '14px 16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: 20, marginBottom: 6 }}>{c.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#f1f5f9', marginBottom: 5 }}>{c.title}</div>
                  <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>{c.body}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Top 2-col grid (stacks on mobile) ── */}
          <div className="sm-grid">

            {/* ── Insider Trades card ── */}
            <div style={{
              background: 'linear-gradient(145deg, #0f1c30 0%, #111827 100%)',
              border: '1px solid rgba(56,189,248,0.1)', borderRadius: 18, overflow: 'hidden',
            }}>
              {/* Header — clickable to collapse */}
              <div onClick={() => toggleCollapsed('insider-trades')} style={{ padding: '18px 20px 14px', borderBottom: collapsed.has('insider-trades') ? 'none' : '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#f1f5f9' }}>🕵️ Recent Insider Transactions</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>{trades.length} corporate exec trades · Click to expand</div>
                  {!collapsed.has('insider-trades') && (
                    <div style={{ fontSize: 12, color: '#38bdf8', marginTop: 6, lineHeight: 1.5, maxWidth: 320 }}>
                      💡 Insiders must file with the SEC when they buy or sell. A big purchase by a CEO or CFO is one of the strongest signals that insiders believe the stock is undervalued.
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                  {!isPro && <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, background: 'linear-gradient(135deg,#38bdf8,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 6, padding: '2px 7px' }}>PRO</span>}
                  <span style={{ fontSize: 16, color: '#475569' }}>{collapsed.has('insider-trades') ? '▼' : '▲'}</span>
                </div>
              </div>

              {!collapsed.has('insider-trades') && <div style={{ padding: '0 20px' }}>
                {trades.length === 0 && (
                  <div style={{ padding: '20px 0', color: '#475569', fontSize: 13 }}>No recent filings found.</div>
                )}
                {trades.length > 0 && trades.length < 4 && (
                  <div style={{ padding: '10px 14px', marginBottom: 12, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, color: '#94a3b8', fontSize: 12, lineHeight: 1.5 }}>
                    📉 <strong style={{ color: '#c7d2fe' }}>Insider buying is quiet right now.</strong> Only {trades.length} significant open-market purchase{trades.length === 1 ? '' : 's'} in the last 14 days — likely a sign of broader market caution. This updates automatically each morning.
                  </div>
                )}

                {/* PRO: rich trade cards with investor context */}
                {isPro && trades.map((t, i) => {
                  const isBuy = t.code === 'P' || t.transactionType === 'Buy';
                  const isSell = t.code === 'S' || t.transactionType === 'Sell';
                  const typeLabel = isBuy ? 'BUY' : isSell ? 'SELL' : 'AWARD';
                  const typeArrow = isBuy ? '↑' : isSell ? '↓' : '•';
                  const typeBg = isBuy ? 'rgba(34,197,94,0.12)' : isSell ? 'rgba(239,68,68,0.12)' : 'rgba(99,102,241,0.12)';
                  const typeColor = isBuy ? '#22c55e' : isSell ? '#ef4444' : '#818cf8';
                  const typeBorder = isBuy ? 'rgba(34,197,94,0.25)' : isSell ? 'rgba(239,68,68,0.25)' : 'rgba(99,102,241,0.25)';
                  const amtColor = isBuy ? '#22c55e' : isSell ? '#ef4444' : '#818cf8';
                  const sharesStr = t.shares ? t.shares.toLocaleString() + ' shares' : '';
                  const priceStr = t.priceFormatted ? '@ ' + t.priceFormatted + '/share' : '';
                  const whyItMatters = isBuy
                    ? `Insiders can only buy their own stock if they think it\'s undervalued. Spending ${t.totalFormatted || 'this much'} of their own money is a strong vote of confidence.`
                    : isSell
                    ? `Insider sells happen for many reasons — taxes, diversification, personal expenses. One sell alone is not a red flag, but it\'s worth noting.`
                    : `Stock awards are part of executive compensation. This is not an open-market trade.`;
                  return (
                    <div key={i} style={{ margin: '10px 0', padding: '14px 16px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${isBuy ? 'rgba(34,197,94,0.15)' : isSell ? 'rgba(239,68,68,0.12)' : 'rgba(99,102,241,0.12)'}`, borderRadius: 12 }}>
                      {/* Top row: avatar + company + trade badge + amount */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={tradeAvatarStyle(t)}>{tradeLabel(t)}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#e2e8f0' }}>{t.company}</span>
                            {t.ticker && <span style={{ fontSize: 11, fontWeight: 700, color: '#38bdf8', background: 'rgba(56,189,248,0.1)', padding: '1px 6px', borderRadius: 5 }}>{t.ticker}</span>}
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 7, background: typeBg, color: typeColor, border: `1px solid ${typeBorder}` }}>{typeLabel} {typeArrow}</span>
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                            <span style={{ color: '#94a3b8', fontWeight: 600 }}>{t.insider || 'Insider'}</span>
                            {t.title && t.title !== 'Insider' && <span style={{ color: '#475569' }}> · {t.title}</span>}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: amtColor }}>{t.totalFormatted || '—'}</div>
                          <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{t.date}</div>
                        </div>
                      </div>
                      {/* Details row: shares + price */}
                      {(sharesStr || priceStr) && (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                          {sharesStr && <span>{sharesStr}</span>}
                          {sharesStr && priceStr && <span style={{ color: '#334155' }}> · </span>}
                          {priceStr && <span>{priceStr}</span>}
                        </div>
                      )}
                      {/* Why it matters */}
                      <div style={{ marginTop: 8, fontSize: 12, color: isBuy ? '#4ade80' : isSell ? '#f87171' : '#818cf8', lineHeight: 1.5, borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: 8 }}>
                        💡 {whyItMatters}
                        {t.secUrl && <a href={t.secUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#38bdf8', textDecoration: 'none', marginLeft: 8, fontSize: 11 }}>SEC Filing →</a>}
                      </div>
                    </div>
                  );
                })}

                {/* FREE: first 3 trades with masked amounts, then blur wall */}
                {!isPro && (
                  <>
                    {freeTrades.map((t, i) => (
                      <div key={i} style={{ margin: '10px 0', padding: '14px 16px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={tradeAvatarStyle(t)}>{tradeLabel(t)}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 15, fontWeight: 700, color: '#e2e8f0' }}>{t.company}</span>
                              {t.ticker && <span style={{ fontSize: 11, fontWeight: 700, color: '#38bdf8', background: 'rgba(56,189,248,0.1)', padding: '1px 6px', borderRadius: 5 }}>{t.ticker}</span>}
                              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 7, background: 'rgba(34,197,94,0.12)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.25)' }}>BUY ↑</span>
                            </div>
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                              <span style={{ color: '#94a3b8', fontWeight: 600 }}>{t.insider || 'Insider'}</span>
                              {t.title && t.title !== 'Insider' && <span style={{ color: '#475569' }}> · {t.title}</span>}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#334155', background: 'rgba(51,65,85,0.4)', borderRadius: 8, padding: '3px 10px', letterSpacing: 2 }}>$••••</div>
                            <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{t.date}</div>
                          </div>
                        </div>
                        <div style={{ marginTop: 8, fontSize: 12, color: '#4ade80', lineHeight: 1.5, borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: 8 }}>
                          💡 Insiders can only buy their own stock if they believe it is undervalued. This is a signal worth watching.
                        </div>
                      </div>
                    ))}
                    <div style={{ position: 'relative' }}>
                      <div style={{ filter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.45 }}>
                        {GHOST_TRADES.map((g, i) => (
                          <div key={i} className="sm-trade-row">
                            <div style={avatarStyle(g.company)}>{initials(g.company)}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>{g.company}</div>
                              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{g.insider}</div>
                            </div>
                            <div className="sm-trade-right">
                              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 7, background: 'rgba(34,197,94,0.12)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.25)' }}>BUY</span>
                              <span style={{ fontSize: 14, fontWeight: 800, color: '#22c55e' }}>$1,240,000</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{
                        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        background: 'linear-gradient(to bottom, transparent, rgba(8,15,30,0.93))',
                        borderRadius: 8, zIndex: 2, padding: 16, textAlign: 'center',
                      }}>
                        <div style={{ fontSize: 24, marginBottom: 6 }}>🔒</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#f1f5f9', marginBottom: 6 }}>See the dollar amounts</div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>+12 more trades filtered to C-suite buys over $250K</div>
                        <Link to="/pricing" style={{ padding: '9px 20px', background: 'linear-gradient(135deg,#38bdf8,#a78bfa)', borderRadius: 9, color: '#fff', fontWeight: 800, fontSize: 13, textDecoration: 'none' }}>
                          Unlock Pro
                        </Link>
                      </div>
                    </div>
                  </>
                )}

                {/* Share bar — dynamic text based on actual trades */}
                <div style={{ margin: '16px 0 4px', padding: '14px 16px', background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.12)', borderRadius: 12 }}>
                  <p style={{ color: '#38bdf8', fontSize: 13, fontWeight: 700, margin: '0 0 10px' }}>📤 Share this tracker</p>
                  <ShareBar
                    text={(() => {
                      const tradesArr = trades || [];
                      if (tradesArr.length === 0) return "Tracking insider buys and top hedge fund moves in real time — KnowFi Smart Money";
                      const topTrade = tradesArr[0];
                      const name = topTrade.insider ? topTrade.insider.split(' ').slice(-1)[0] : 'Insider';
                      const company = topTrade.company || 'a major company';
                      const amt = topTrade.totalFormatted || '';
                      const count = tradesArr.length;
                      const topIsBuy = topTrade.code === 'P' || topTrade.transactionType === 'Buy';
                      const action = topIsBuy ? 'bought' : 'sold';
                      const moveWord = topIsBuy ? 'purchases' : 'trades';
                      return `${name} just ${action} ${amt ? amt + ' of ' : ''}${company} stock -- plus ${count > 1 ? count - 1 + ' more insider' : 'more'} ${moveWord} tracked live on KnowFi Smart Money`;
                    })()}
                    url="https://knowfi.io/smart-money"
                  />
                  {!isPro && (
                    <p style={{ color: '#475569', fontSize: 11, margin: '10px 0 0' }}>
                      Want dollar amounts + all hedge fund holdings?{' '}
                      <Link to="/pricing" style={{ color: '#38bdf8', textDecoration: 'none', fontWeight: 700 }}>Upgrade to Pro →</Link>
                    </p>
                  )}
                </div>
              </div>}
            </div>

            {/* ── Berkshire card ── */}
            {hedgeFunds.length > 0 && (() => {
              const berk = hedgeFunds.find(f => f.fund?.toLowerCase().includes('berkshire')) || hedgeFunds[0];
              const topFree = berk.topHoldings?.slice(0, 5) || [];
              const topLocked = berk.topHoldings?.slice(5, 10) || [];
              return (
                <div id="fund-berkshire-hathaway" style={{ background: 'linear-gradient(145deg, #0f1c30 0%, #111827 100%)', border: '1px solid rgba(56,189,248,0.1)', borderRadius: 18, overflow: 'hidden' }}>
                  <div onClick={() => toggleCollapsed('berkshire-hathaway')} style={{ padding: '18px 20px 14px', borderBottom: collapsed.has('berkshire-hathaway') ? 'none' : '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#f1f5f9' }}>{berk.icon} {berk.fund}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>{berk.manager} · {collapsed.has('berkshire-hathaway') ? 'Click to expand' : `Top 10 of ${(berk.holdingsCount || berk.topHoldings?.length || 0).toLocaleString()} positions`}</div>
                      {(berk.lastFiling || berk.previousFilingDate) && (
                        <div style={{ fontSize: 11, color: '#475569', marginTop: 5 }}>
                          {berk.lastFiling && <>📅 Latest 13F: <span style={{ color: '#94a3b8', fontWeight: 600 }}>{berk.lastFiling}</span></>}
                          {berk.previousFilingDate && <span style={{ color: '#3f4f5f' }}> · vs. {berk.previousFilingDate}</span>}
                        </div>
                      )}
                      {berk.cashOnHand && (
                        <div style={{ marginTop: 5, fontSize: 12, color: '#34d399', fontWeight: 700 }}>💰 Cash on hand: {berk.cashOnHand} <span style={{ color: '#475569', fontWeight: 400, fontSize: 11 }}>({berk.cashAsOf})</span></div>
                      )}
                      {!collapsed.has('berkshire-hathaway') && (
                        <div style={{ fontSize: 12, color: '#4ade80', marginTop: 6, lineHeight: 1.5, maxWidth: 380 }}>
                          💡 Buffett's portfolio is the most-studied in investing. He buys companies he plans to hold for <em>decades</em>. When he adds or trims a position, millions of investors pay attention — and for good reason.
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <div style={{ fontSize: 18, fontWeight: 900, color: '#38bdf8' }}>{berk.totalValue}</div>
                      {!isPro && <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, background: 'linear-gradient(135deg,#38bdf8,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 6, padding: '2px 7px', display: 'inline-block' }}>PRO</span>}
                      <span style={{ fontSize: 16, color: '#475569', marginTop: 4 }}>{collapsed.has('berkshire-hathaway') ? '▼' : '▲'}</span>
                    </div>
                  </div>
                  {!collapsed.has('berkshire-hathaway') && <>
                  {/* Berkshire position changes vs prior filing */}
                  {berk.recentChanges?.length > 0 && (
                    <div style={{ padding: '12px 20px 8px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 10 }}>
                        🔄 What changed since last quarter
                      </div>
                      {berk.recentChanges.map((m, mi) => {
                        const isUp = m.changeType === 'Increased' || m.changeType === 'New Position';
                        const isNew = m.changeType === 'New Position';
                        const isExit = m.changeType === 'Exited';
                        const accent = isUp ? '#22c55e' : '#f87171';
                        const bg = isUp ? 'rgba(34,197,94,0.06)' : 'rgba(248,113,113,0.06)';
                        const badge = isNew ? { label: 'Brand New', emoji: '🆕' }
                          : isExit ? { label: 'Sold Out', emoji: '🚪' }
                          : isUp ? { label: 'Bought More', emoji: '↑' }
                          : { label: 'Trimmed', emoji: '↓' };
                        const summary = isNew
                          ? `Opened a new ${m.currentValueFormatted} position`
                          : isExit
                          ? `Completely sold their ${m.changeFormatted} stake`
                          : isUp
                          ? `Added ${m.changeFormatted}${m.pctChange ? ` (${m.pctChange})` : ''} — now ${m.currentValueFormatted} total`
                          : `Sold ${m.changeFormatted}${m.pctChange ? ` (${m.pctChange})` : ''} — ${m.currentValueFormatted} remaining`;
                        return (
                          <div key={mi} style={{ display: 'flex', gap: 10, marginBottom: 8, background: bg, border: `1px solid ${accent}22`, borderRadius: 10, padding: '8px 12px', alignItems: 'flex-start' }}>
                            <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>{badge.emoji}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>{m.name}</span>
                                <span style={{ fontSize: 10, fontWeight: 800, color: accent, textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>{badge.label}</span>
                              </div>
                              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3, lineHeight: 1.4 }}>{summary}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ padding: '0 20px' }}>
                    {topFree.map((h, i) => (
                      <div key={i} className="sm-holding-row">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</div>
                          <div style={{ height: 4, background: 'rgba(56,189,248,0.12)', borderRadius: 2, marginTop: 5, overflow: 'hidden' }}>
                            <div style={{ height: '100%', borderRadius: 2, background: 'linear-gradient(90deg,#38bdf8,#a78bfa)', width: h.pct }} />
                          </div>
                        </div>
                        <div className="sm-holding-meta">
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#a78bfa' }}>{h.pct}</span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#38bdf8' }}>{h.valueFormatted}</span>
                        </div>
                      </div>
                    ))}

                    {isPro ? (
                      topLocked.map((h, i) => (
                        <div key={i} className="sm-holding-row">
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</div>
                            <div style={{ height: 4, background: 'rgba(56,189,248,0.12)', borderRadius: 2, marginTop: 5, overflow: 'hidden' }}>
                              <div style={{ height: '100%', borderRadius: 2, background: 'linear-gradient(90deg,#38bdf8,#a78bfa)', width: h.pct }} />
                            </div>
                          </div>
                          <div className="sm-holding-meta">
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#a78bfa' }}>{h.pct}</span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#38bdf8' }}>{h.valueFormatted}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ position: 'relative' }}>
                        <div style={{ filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.4 }}>
                          {GHOST_HOLDINGS.map((g, i) => (
                            <div key={i} className="sm-holding-row">
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, color: '#cbd5e1' }}>{g.name}</div>
                              </div>
                              <div className="sm-holding-meta">
                                <span style={{ fontSize: 13, color: '#a78bfa' }}>{g.pct}</span>
                                <span style={{ fontSize: 13, color: '#38bdf8' }}>{g.valueFormatted}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom, transparent, rgba(8,15,30,0.94))', borderRadius: 8, padding: 14, textAlign: 'center' }}>
                          <div style={{ fontSize: 24, marginBottom: 6 }}>🔒</div>
                          <div style={{ fontSize: 14, fontWeight: 800, color: '#f1f5f9', marginBottom: 6 }}>Full portfolio</div>
                          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>5 more positions with % + $ value</div>
                          <Link to="/pricing" style={{ padding: '9px 20px', background: 'linear-gradient(135deg,#38bdf8,#a78bfa)', borderRadius: 9, color: '#fff', fontWeight: 800, fontSize: 13, textDecoration: 'none' }}>
                            Unlock Pro
                          </Link>
                        </div>
                      </div>
                    )}

                    <div style={{ margin: '16px 0 4px', padding: '14px 16px', background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.12)', borderRadius: 12 }}>
                      <p style={{ color: '#38bdf8', fontSize: 13, fontWeight: 700, margin: '0 0 10px' }}>📤 Share Berkshire's portfolio</p>

                      <ShareBar
                        text={(() => {
                          const berk = hedgeFunds?.find(f => f.fund?.toLowerCase().includes('berkshire'));
                          const topHolding = berk?.holdings?.[0];
                          return topHolding
                            ? `Warren Buffett's Berkshire Hathaway holds ${topHolding.value || ''} in ${topHolding.ticker || topHolding.company} — see all his top holdings on KnowFi Smart Money`
                            : `Warren Buffett's Berkshire Hathaway — see what he owns on KnowFi Smart Money`;
                        })()}
                        url="https://knowfi.io/smart-money?fund=berkshire-hathaway"
                      />
                      {!isPro && (
                        <p style={{ color: '#475569', fontSize: 11, margin: '10px 0 0' }}>
                          See all 10 positions with dollar values.{' '}
                          <Link to="/pricing" style={{ color: '#38bdf8', textDecoration: 'none', fontWeight: 700 }}>Upgrade to Pro →</Link>
                        </p>
                      )}
                    </div>
                  </div>
                  </>}
                </div>
              );
            })()}
          </div>

          {/* ── Hedge Fund Grid ── */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ padding: '0 4px 16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#f1f5f9' }}>🏦 Top Hedge Fund Portfolios</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>Latest quarterly 13F filings · % and $ value for every holding</div>
                <div style={{ fontSize: 12, color: '#a78bfa', marginTop: 6, lineHeight: 1.5, maxWidth: 480 }}>
                  💡 Every quarter, hedge funds with $100M+ must tell the SEC exactly what they own. These are the stocks <em>professionals</em> are holding with conviction — not day-trading, holding. A stock that shows up across multiple funds is worth paying attention to.
                </div>
              </div>
              {!isPro && <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, background: 'linear-gradient(135deg,#38bdf8,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 6, padding: '2px 7px' }}>PRO</span>}
            </div>

            <div className="sm-fund-grid">
              {hedgeFunds.filter(f => !f.fund?.toLowerCase().includes('berkshire')).map((fund, fi) => {
                const fundId = fund.fund?.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'');
                const isCollapsed = collapsed.has(fundId);
                const topFree = fund.topHoldings?.slice(0, 3) || [];
                const topLocked = fund.topHoldings?.slice(3, 10) || [];
                const totalPositions = fund.holdingsCount || fund.topHoldings?.length || 0;
                return (
                  <div key={fi} id={`fund-${fundId}`} style={{ background: 'linear-gradient(145deg, #0d1c32 0%, #0f172a 100%)', border: '1px solid rgba(56,189,248,0.08)', borderRadius: 16, overflow: 'hidden' }}>
                    {/* Fund header — clickable to collapse */}
                    <div onClick={() => toggleCollapsed(fundId)} style={{ padding: '16px 18px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: isCollapsed ? 'none' : '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', userSelect: 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 24 }}>{fund.icon}</span>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 800, color: '#f1f5f9' }}>{fund.fund}</div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{fund.manager} · Top 10 of {totalPositions.toLocaleString()} positions</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                        <div style={{ fontSize: 17, fontWeight: 900, color: '#38bdf8' }}>{fund.totalValue}</div>
                        <span style={{ fontSize: 14, color: '#475569', marginTop: 2 }}>{isCollapsed ? '▼' : '▲'}</span>
                      </div>
                    </div>

                    {!isCollapsed && <>
                    {/* Filing dates + cash on hand row */}
                    <div style={{ padding: '7px 18px', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                      {fund.lastFiling && <span style={{ fontSize: 11, color: '#475569' }}>📅 Latest 13F: <span style={{ color: '#94a3b8', fontWeight: 600 }}>{fund.lastFiling}</span></span>}
                      {fund.previousFilingDate && <span style={{ fontSize: 11, color: '#475569' }}>vs. <span style={{ color: '#64748b' }}>{fund.previousFilingDate}</span></span>}
                      {fund.cashOnHand && <span style={{ fontSize: 11, color: '#34d399', fontWeight: 700, marginLeft: 'auto' }}>💰 Cash: {fund.cashOnHand}</span>}
                    </div>

                    {/* Position changes vs prior 13F */}
                    {fund.recentChanges?.length > 0 && (
                      <div style={{ padding: '12px 18px 8px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 10 }}>
                          🔄 What changed since last quarter
                        </div>
                        {fund.recentChanges.map((m, mi) => {
                          const isUp = m.changeType === 'Increased' || m.changeType === 'New Position';
                          const isNew = m.changeType === 'New Position';
                          const isExit = m.changeType === 'Exited';
                          const accent = isUp ? '#22c55e' : '#f87171';
                          const bg = isUp ? 'rgba(34,197,94,0.06)' : 'rgba(248,113,113,0.06)';
                          const badge = isNew ? { label: 'Brand New', emoji: '🆕' }
                            : isExit ? { label: 'Sold Out', emoji: '🚪' }
                            : isUp ? { label: 'Bought More', emoji: '↑' }
                            : { label: 'Trimmed', emoji: '↓' };
                          const summary = isNew
                            ? `Opened a new ${m.currentValueFormatted} position`
                            : isExit
                            ? `Completely sold their ${m.changeFormatted} stake`
                            : isUp
                            ? `Added ${m.changeFormatted}${m.pctChange ? ` (${m.pctChange})` : ''} — now ${m.currentValueFormatted} total`
                            : `Sold ${m.changeFormatted}${m.pctChange ? ` (${m.pctChange})` : ''} — ${m.currentValueFormatted} remaining`;
                          return (
                            <div key={mi} style={{ display: 'flex', gap: 10, marginBottom: 8, background: bg, border: `1px solid ${accent}22`, borderRadius: 10, padding: '8px 12px', alignItems: 'flex-start' }}>
                              <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>{badge.emoji}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>{m.name}</span>
                                  <span style={{ fontSize: 10, fontWeight: 800, color: accent, textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>{badge.label}</span>
                                </div>
                                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3, lineHeight: 1.4 }}>{summary}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Free holdings: % + $ */}
                    <div style={{ padding: '0 18px' }}>
                      {topFree.map((h, i) => (
                        <div key={i} className="sm-holding-row">
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</div>
                            <div style={{ height: 3, background: 'rgba(56,189,248,0.1)', borderRadius: 2, marginTop: 5, overflow: 'hidden' }}>
                              <div style={{ height: '100%', borderRadius: 2, background: 'linear-gradient(90deg,#38bdf8,#a78bfa)', width: h.pct }} />
                            </div>
                          </div>
                          <div className="sm-holding-meta">
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#a78bfa' }}>{h.pct}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#38bdf8' }}>{h.valueFormatted}</span>
                          </div>
                        </div>
                      ))}

                      {/* Locked holdings OR pro view */}
                      {isPro ? (
                        topLocked.map((h, i) => (
                          <div key={i} className="sm-holding-row">
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#cbd5e1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.name}</div>
                              <div style={{ height: 3, background: 'rgba(56,189,248,0.1)', borderRadius: 2, marginTop: 5, overflow: 'hidden' }}>
                                <div style={{ height: '100%', borderRadius: 2, background: 'linear-gradient(90deg,#38bdf8,#a78bfa)', width: h.pct }} />
                              </div>
                            </div>
                            <div className="sm-holding-meta">
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#a78bfa' }}>{h.pct}</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#38bdf8' }}>{h.valueFormatted}</span>
                            </div>
                          </div>
                        ))
                      ) : topLocked.length > 0 ? (
                        <div style={{ position: 'relative' }}>
                          <div style={{ filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.3 }}>
                            {GHOST_HOLDINGS.map((g, i) => (
                              <div key={i} className="sm-holding-row">
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, color: '#cbd5e1' }}>{g.name}</div>
                                </div>
                                <div className="sm-holding-meta">
                                  <span style={{ fontSize: 12, color: '#a78bfa' }}>{g.pct}</span>
                                  <span style={{ fontSize: 12, color: '#38bdf8' }}>{g.valueFormatted}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom, transparent, rgba(8,15,30,0.95))', borderRadius: 6, padding: 12, textAlign: 'center' }}>
                            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>+{topLocked.length} more positions locked</div>
                            <Link to="/pricing" style={{ padding: '7px 16px', background: 'linear-gradient(135deg,#38bdf8,#a78bfa)', borderRadius: 8, color: '#fff', fontWeight: 800, fontSize: 12, textDecoration: 'none' }}>
                              Unlock Pro →
                            </Link>
                          </div>
                        </div>
                      ) : null}

                      {/* Per-fund share bar */}
                      <div style={{ margin: '14px 0 4px', padding: '14px 16px', background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.12)', borderRadius: 12 }}>
                        <p style={{ color: '#38bdf8', fontSize: 12, fontWeight: 700, margin: '0 0 10px' }}>📤 Share {fund.fund}'s portfolio</p>
                        <ShareBar
                          text={`${fund.fund} (${fund.manager}) holds ${fund.totalValue} — see what they own on KnowFi Smart Money`}
                          url={`https://knowfi.io/smart-money?fund=${encodeURIComponent(fund.fund?.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,''))}`}
                        />
                        {!isPro && (
                          <p style={{ color: '#475569', fontSize: 11, margin: '10px 0 0' }}>
                            See full holdings with dollar values.{' '}
                            <Link to="/pricing" style={{ color: '#38bdf8', textDecoration: 'none', fontWeight: 700 }}>Upgrade to Pro →</Link>
                          </p>
                        )}
                      </div>
                    </div>
                    </>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Upgrade banner (free only) ── */}
          {!isPro && (
            <div style={{
              background: 'linear-gradient(135deg, #0f1f3d 0%, #1a0f35 100%)',
              border: '1px solid rgba(167,139,250,0.25)', borderRadius: 18,
              padding: '32px 24px', textAlign: 'center', marginTop: 8,
            }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#f1f5f9', marginBottom: 10 }}>
                You're seeing 10% of the picture
              </div>
              <p style={{ fontSize: 15, color: '#94a3b8', maxWidth: 480, margin: '0 auto 22px', lineHeight: 1.6 }}>
                Pro unlocks dollar amounts on every insider trade, full hedge fund portfolios, and a daily alert when big money moves.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginBottom: 24 }}>
                {['💰 Dollar amounts on every trade','📊 Full hedge fund portfolios','🔥 $250K+ conviction filter','📅 Daily refresh','🏛️ 5 elite funds tracked'].map(f => (
                  <span key={f} style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 20, padding: '6px 14px', fontSize: 13, color: '#38bdf8', fontWeight: 600 }}>{f}</span>
                ))}
              </div>
              <Link to="/pricing" style={{ display: 'inline-block', padding: '14px 36px', background: 'linear-gradient(135deg,#38bdf8,#a78bfa)', borderRadius: 12, color: '#fff', fontWeight: 900, fontSize: 16, textDecoration: 'none', boxShadow: '0 0 30px rgba(56,189,248,0.2)' }}>
                Unlock Smart Money Pro
              </Link>
              <div style={{ marginTop: 12, fontSize: 13, color: '#475569' }}>$12/month · Cancel anytime</div>
            </div>
          )}

          {/* ── How to use this data ── */}
          <div style={{ marginTop: 40, marginBottom: 8 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#f1f5f9', marginBottom: 20 }}>❓ How to use this data</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
              {[
                {
                  q: 'Should I just copy these trades?',
                  a: 'Not blindly. Hedge funds have different goals and timelines than you. Use this as a research starting point — if a stock shows up across multiple funds and an exec is also buying, that\'s worth digging into.',
                },
                {
                  q: 'Why do these filings show up late?',
                  a: 'Hedge funds file 13Fs up to 45 days after the end of each quarter. Insider Form 4 filings must be submitted within 2 business days of a trade — those are much more timely.',
                },
                {
                  q: 'What does "portfolio %" mean?',
                  a: 'It shows how much of the fund\'s total portfolio is in that one stock. A 15% position means it\'s a major bet — they really believe in it. Under 1% is more of a small exploratory position.',
                },
                {
                  q: 'What should I actually do with this?',
                  a: 'Think of it as a research shortlist. If you see a company you\'ve been curious about showing up in multiple fund portfolios with growing position sizes, that\'s a signal worth researching further.',
                },
                {
                  q: 'Are insiders always right?',
                  a: 'No — but they\'re the best-informed people about their own company. Studies consistently show insider buying predicts above-average stock returns more often than not. Insider selling is less reliable (they sell for many reasons).',
                },
                {
                  q: 'What\'s a "Form 4"?',
                  a: 'It\'s the SEC form corporate insiders (CEOs, CFOs, directors) must file within 2 business days when they buy or sell company stock. It\'s public record — this page surfaces the most significant recent filings.',
                },
              ].map((item, i) => (
                <div key={i} style={{ background: '#111827', border: '1px solid #1e293b', borderRadius: 12, padding: '16px 18px' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#f1f5f9', marginBottom: 8 }}>{item.q}</div>
                  <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.65 }}>{item.a}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}
      {/* Financial disclaimer */}
      <div style={{ maxWidth: 1100, margin: '32px auto 0', padding: '0 16px 40px' }}>
        <div style={{ padding: '12px 16px', background: 'rgba(100,116,139,0.06)', border: '1px solid #1e293b', borderRadius: 10, fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
          <strong style={{ color: '#64748b' }}>Disclaimer:</strong> The information on this page is for educational purposes only and is not financial, investment, or tax advice. SEC filings are public data; displaying them does not constitute a recommendation to buy or sell any security. Always consult a qualified financial professional before making investment decisions.
        </div>
      </div>
    </div>
  );
}
